import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PanelEntity } from './EntitySidePanel';
import { VocabData, EMPTY_VOCAB, BASE_REL_TYPES, pairKey, entityTypeToPairSegment } from '../constants/vocab';

export interface PanelRelationship {
  entityId: string;
  relationshipType: string;
  description: string;
  metadata: Record<string, unknown>;
  directed?: boolean;
}

export interface RelationshipUpdate {
  relationshipType?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  directed?: boolean;
}

interface RelationshipPanelProps {
  relationship: PanelRelationship;
  sourceEntity: PanelEntity;
  targetEntity: PanelEntity;
  onClose: () => void;
  onSave: (relId: string, updates: RelationshipUpdate) => Promise<void>;
  onDelete: (relId: string) => void;
  onSwap?: (relId: string) => void;
  vocab?: VocabData;
}

const ENTITY_COLORS: Record<string, string> = {
  character: '#196773',
  location: '#5CA383',
  event: '#c97a1e',
};

const ENTITY_LABELS: Record<string, string> = {
  character: 'Character',
  location: 'Location',
  event: 'Event',
};

const REL_STATUS_OPTIONS = [
  'active', 'new', 'growing', 'complicated', 'volatile', 'tense',
  'strained', 'fractured', 'doomed', 'reconciling', 'severed', 'toxic',
];

const RelationshipPanel: React.FC<RelationshipPanelProps> = ({
  relationship, sourceEntity, targetEntity,
  onClose, onSave, onDelete, onSwap, vocab = EMPTY_VOCAB,
}) => {
  const [localType, setLocalType] = useState(relationship.relationshipType);
  const [localDesc, setLocalDesc] = useState(relationship.description);
  const [localMeta, setLocalMeta] = useState<Record<string, unknown>>(relationship.metadata ?? {});
  const [localDirected, setLocalDirected] = useState(relationship.directed ?? false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdates = useRef<RelationshipUpdate>({});
  /* The relationship the pending edits were made on. Switching to another relationship,
     or closing the panel, inside the debounce used to drop them; they now go to this
     one, at once. (#965) */
  const pendingFor = useRef('');
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  /* Send any pending edits now, to the relationship they were made on. The status line
     is left alone: by now the panel may be showing another one, or be gone. */
  const flushPending = useCallback(() => {
    if (!debounceTimer.current) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
    const id = pendingFor.current;
    const updates = pendingUpdates.current;
    pendingUpdates.current = {};
    onSaveRef.current(id, updates).catch(e => console.error('Failed to save edits to', id, e));
  }, []);

  useEffect(() => {
    flushPending();
    setLocalType(relationship.relationshipType);
    setLocalDesc(relationship.description);
    setLocalMeta(relationship.metadata ?? {});
    setLocalDirected(relationship.directed ?? false);
    setConfirmDelete(false);
    setSaveStatus('idle');
  }, [relationship.entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* On delete, edits still waiting are for a record about to go. Sending them after the
     delete, as closing the panel would, writes it back: the POST is an upsert. */
  const dropPending = () => {
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    pendingUpdates.current = {};
  };

  useEffect(() => () => {
    flushPending();
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, [flushPending]);

  const markSaved = () => {
    setSaveStatus('saved');
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const scheduleUpdate = (partial: RelationshipUpdate) => {
    pendingFor.current = relationship.entityId;
    pendingUpdates.current = {
      ...pendingUpdates.current,
      ...partial,
      ...(partial.metadata
        ? { metadata: { ...(pendingUpdates.current.metadata ?? {}), ...(partial.metadata as Record<string, unknown>) } }
        : {}),
    };
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSaveStatus('saving');
    debounceTimer.current = setTimeout(async () => {
      debounceTimer.current = null;
      const updates = { ...pendingUpdates.current };
      pendingUpdates.current = {};
      try { await onSave(pendingFor.current, updates); markSaved(); }
      catch { setSaveStatus('error'); }
    }, 1000);
  };

  const saveNow = async (updates: RelationshipUpdate) => {
    /* Take the waiting edits along rather than leaving the debounce to write them again a
       second later, as the entity panel does (#782). */
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null; }
    const merged = { ...pendingUpdates.current, ...updates };
    pendingUpdates.current = {};
    setSaveStatus('saving');
    try { await onSave(relationship.entityId, merged); markSaved(); }
    catch { setSaveStatus('error'); }
  };

  const handleMetaChange = (key: string, value: unknown) => {
    const next = { ...localMeta, [key]: value };
    setLocalMeta(next);
    scheduleUpdate({ metadata: { [key]: value } });
  };

  const relTypePairK = pairKey(
    entityTypeToPairSegment(sourceEntity.entityType),
    entityTypeToPairSegment(targetEntity.entityType)
  );
  const relTypeBaseOpts = BASE_REL_TYPES[relTypePairK] ?? [];
  const relTypeCustomOpts = vocab.customRelTypes[relTypePairK] ?? [];
  const relTypeOpts = [...relTypeBaseOpts, ...relTypeCustomOpts];
  const isLegacyType = !!localType && !relTypeOpts.includes(localType);

  const statusText = saveStatus === 'saving' ? 'Saving…'
    : saveStatus === 'saved' ? 'Saved ✓'
    : saveStatus === 'error' ? 'Save failed'
    : '';

  return (
    <div className="relationship-panel">

      <div className="relationship-panel-header">
        <div className="relationship-panel-header-top">
          <span className="relationship-panel-save-status">{statusText}</span>
          <button className="relationship-panel-close" aria-label="Close panel" onClick={onClose}>✕</button>
        </div>
        <div className="relationship-panel-entities">
          <span className="relationship-panel-entity-badge" style={{ background: ENTITY_COLORS[sourceEntity.entityType] }}>
            {ENTITY_LABELS[sourceEntity.entityType]}
          </span>
          <span className="relationship-panel-entity-name">{sourceEntity.name}</span>
          <span className="relationship-panel-arrow">{localDirected ? '→' : '↔'}</span>
          <span className="relationship-panel-entity-badge" style={{ background: ENTITY_COLORS[targetEntity.entityType] }}>
            {ENTITY_LABELS[targetEntity.entityType]}
          </span>
          <span className="relationship-panel-entity-name">{targetEntity.name}</span>
        </div>
      </div>

      <div className="relationship-panel-section">
        <div className="relationship-panel-section-title">Core</div>
        <div className="relationship-panel-field">
          <label>Type
            <select
              value={localType}
              onChange={async e => { const v = e.target.value; setLocalType(v); await saveNow({ relationshipType: v }); }}
            >
              {relTypeOpts.map(o => <option key={o} value={o}>{o}</option>)}
              {isLegacyType && <option value={localType}>{localType} (legacy)</option>}
            </select>
          </label>
        </div>
        <div className="relationship-panel-field">
          <label>Direction</label>
          <div className="relationship-direction-toggle">
            <button
              type="button"
              className={`relationship-direction-btn${!localDirected ? ' active' : ''}`}
              onClick={async () => { setLocalDirected(false); await saveNow({ directed: false }); }}
            >
              ↔ Bidirectional
            </button>
            <button
              type="button"
              className={`relationship-direction-btn${localDirected ? ' active' : ''}`}
              onClick={async () => { setLocalDirected(true); await saveNow({ directed: true }); }}
            >
              → Directed
            </button>
          </div>
          {localDirected && (
            <button
              type="button"
              className="relationship-swap-btn"
              onClick={async () => {
                /* The swap rebuilds the relationship from its last saved copy, so edits
                   still waiting (a field with no blur save) are saved first, or they
                   would be sent to the old one after it is gone. (#965) */
                if (debounceTimer.current) await saveNow({});
                onSwap?.(relationship.entityId);
              }}
            >
              Swap relationship direction
            </button>
          )}
        </div>
        <div className="relationship-panel-field">
          <label>Description
            <textarea
              maxLength={300}
              value={localDesc}
              onChange={e => { setLocalDesc(e.target.value); scheduleUpdate({ description: e.target.value }); }}
              onBlur={() => { if (localDesc !== relationship.description) saveNow({ description: localDesc }); }}
            />
          </label>
        </div>
      </div>

      <div className="relationship-panel-section">
        <div className="relationship-panel-section-title">Narrative</div>
        <div className="relationship-panel-field">
          <label>How it formed
            <textarea
              maxLength={300}
              value={(localMeta.origin as string) ?? ''}
              onChange={e => handleMetaChange('origin', e.target.value)}
            />
          </label>
        </div>
        <div className="relationship-panel-field">
          <label>Status
            <select
              value={(localMeta.status as string) ?? ''}
              onChange={async e => {
                const v = e.target.value;
                const newMeta = { ...localMeta, status: v };
                setLocalMeta(newMeta);
                await saveNow({ metadata: newMeta });
              }}
            >
              <option value="">— not set —</option>
              {REL_STATUS_OPTIONS.map(o =>
                <option key={o} value={o}>{o}</option>
              )}
            </select>
          </label>
        </div>
        <div className="relationship-panel-field">
          <label>Story impact
            <textarea
              maxLength={300}
              value={(localMeta.storyImpact as string) ?? ''}
              onChange={e => handleMetaChange('storyImpact', e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="relationship-panel-footer">
        {confirmDelete ? (
          <div className="relationship-panel-delete-confirm">
            <span>Delete this relationship? This cannot be undone.</span>
            <button className="btn-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn-danger" onClick={() => { dropPending(); onDelete(relationship.entityId); }}>Delete</button>
          </div>
        ) : (
          <button className="btn-danger-outline" onClick={() => setConfirmDelete(true)}>
            Delete Relationship
          </button>
        )}
      </div>
    </div>
  );
};

export default RelationshipPanel;
